import { Injectable } from "@angular/core";
import { AuviousRtcService } from "./rtc.service";
import { ApiResource } from "@auvious/common";
import { ApplicationService } from "./application.service";
import { GenericErrorHandler } from "./error-handlers.service";

export type AiOptionsUsage =
  | "transcription"
  | "translation"
  | "prompt"
  | "agent"
  | "chat"
  | "recognition";

export interface AiOptionsTestRequest {
  usage: AiOptionsUsage;
  languageCode?: string;
  auviousEnvironment?: string;
}

export interface AiOptionsTestResponse {
  usage: AiOptionsUsage;
  passed: boolean;
  reason?: string;
}

@Injectable({
  providedIn: "root",
})
export class AssistantAPIService {
  private liveResource: ApiResource;

  constructor(
    rtcService: AuviousRtcService,
    private errorHandler: GenericErrorHandler,
    private application: ApplicationService
  ) {
    rtcService.common$.subscribe((common) => {
      this.liveResource = common.apiResourceFactory("api/ai");
    });
  }

  /**
   * Create a session with the AI assistant
   * @param request
   * @returns session id
   */
  public async createSession(request: {
    languageCode?: string;
    username?: string;
    modality: "text" | "audio";
    endpoint?: string;
    promptContext?: string;
    interactionId?: string;
  }): Promise<{ id: string }> {
    return this.liveResource
      .create(request, {
        urlPostfix: `${this.applicationId}/live`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Leaves the session and destroys it
   * @param sessionId
   * @returns void
   */
  public async leaveSession(sessionId: string): Promise<void> {
    try {
      return await this.liveResource.delete({
        urlPostfix: `${this.applicationId}/live/${sessionId}`,
      });
    } catch (ex) {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    }
  }

  /**
   * Joins a session with the AI Assistant. A session must already exist
   * @param sessionId
   * @param conferenceId
   * @returns
   */
  public async joinSession(sessionId: string, conferenceId: string) {
    try {
      return await this.liveResource.create(undefined, {
        urlPostfix: `${this.applicationId}/live/${sessionId}/join/${conferenceId}`,
      });
    } catch (ex) {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    }
  }

  /**
   * Notify the backend which stream track to send to the AI service for analysis
   * @param sessionId
   * @param kind
   * @param streamId
   * @returns
   */
  public connectStreamTrack(
    sessionId: string,
    kind: "audio" | "video",
    streamId: string
  ) {
    return this.liveResource
      .create(undefined, {
        urlPostfix: `${this.applicationId}/live/${sessionId}/${kind}/${streamId}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Stop sending data to the AI service for analysis
   * @param sessionId
   * @param type
   * @param streamId
   * @returns
   */
  public disconnectStreamTrack(
    sessionId: string,
    kind: "audio" | "video",
    streamId: string
  ) {
    return this.liveResource
      .delete({
        urlPostfix: `${this.applicationId}/live/${sessionId}/${kind}/${streamId}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Send a text message to the AI assistant
   * @param sessionId
   * @param message
   * @returns
   */
  public sendMessage(sessionId: string, message: string) {
    return this.liveResource
      .create(
        { message },
        {
          urlPostfix: `${this.applicationId}/live/${sessionId}/text`,
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Test configured AI options against the selected provider.
   * @param request
   * @returns test result for the requested usage
   */
  public async testOptions(
    request: AiOptionsTestRequest
  ): Promise<AiOptionsTestResponse> {
    try {
      return await this.liveResource.create(request, {
        urlPostfix: `${this.applicationId}/options/test`,
      });
    } catch (ex) {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    }
  }

  private get applicationId(): string {
    return this.application.getActiveApplication().getId();
  }
}
